"use client";
import { useState } from "react";
import { Alert, Button, Modal, Table } from "react-bootstrap";
import { MdCancel } from "react-icons/md";
import { AxiosResponse } from "axios";
import type toast from "react-hot-toast";
import ModalIconTitle from "../ModalIconTitle";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { BackendApi } from "../../utils/BackendApi";
import { ApiRoute } from "../../utils/ApiRoute";
import { SystemMsg } from "../../utils/SystemMsg";
import { UserFormData } from "./UserModal";

type DeleteUserModalProps = {
  toast: typeof toast;
  show: boolean;
  data?: UserFormData;
  onDeleted: () => void;
  onClose: () => void;
};

function apiErrorMessage(error: unknown, fallback: string) {
  if (!error || typeof error !== "object") {
    return fallback;
  }
  const responseData = (
    error as { response?: { data?: { message?: unknown } }; data?: { message?: unknown } }
  ).response?.data ?? (error as { data?: { message?: unknown } }).data;
  const message = responseData?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

export default function DeleteUserModal({
  toast,
  show,
  data,
  onDeleted,
  onClose,
}: DeleteUserModalProps) {
  const dialogClassName = useModalWidthClass();
  const [deleting, setDeleting] = useState(false);

  const onHide = () => {
    if (deleting) {
      return;
    }
    onClose();
  };

  const confirmClick = () => {
    if (!data || !Number.isInteger(data.id) || data.id <= 0) {
      toast.error(SystemMsg.User.selectOne);
      return;
    }
    setDeleting(true);
    BackendApi.delete(
      `${ApiRoute.user}/${data.id}`,
      { id: data.id },
      (resp: AxiosResponse) => {
        setDeleting(false);
        if (resp.status !== 200) {
          toast.error(SystemMsg.User.deleteFailed);
          return;
        }
        toast.success(SystemMsg.User.deleteSuccess);
        onDeleted();
        onClose();
      },
      (error: AxiosResponse | string) => {
        setDeleting(false);
        toast.error(apiErrorMessage(error, SystemMsg.User.deleteFailed));
      },
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      dialogClassName={dialogClassName}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <ModalIconTitle title="Delete User" iconElement={<MdCancel />} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          This permanently deletes the user. A user who still has places or
          records cannot be deleted.
        </Alert>
        <Table bordered>
          <tbody>
            <tr>
              <th>Username</th>
              <td>{data?.username}</td>
            </tr>
            <tr>
              <th>Role</th>
              <td>{data?.role}</td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={confirmClick} disabled={deleting}>
          Delete
        </Button>
        <Button variant="secondary" onClick={onHide} disabled={deleting}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
